import {
    type JoinNetworkReqAttributes,
    type JoinPendingRequest,
    type JoinNetworkStartEventTypes,
    type JoinNetworkEndEventTypes,
    type HttpRequestMetrics,
    type JoinEventInput,
    JoinPerfEventTypes,
} from './join-flow-types';
import { getPrefixedLogger, prefixes } from '../rwc-logger';

type JoinNetworkEventInput = Extract<
    JoinEventInput,
    { eventType: JoinNetworkStartEventTypes | JoinNetworkEndEventTypes }
>;

const logger = getPrefixedLogger(prefixes.RWC_UTILS, 'analytics', 'join-flow-tracking');

const POST_BRIDGES = /\/rcvideo\/v2\/account\/\d+\/extension\/\d+\/bridges$/;
const GET_PIN = /\/rcvideo\/v2\/bridges\/pin\/web\/\d+$/;
const POST_MEETING_INFO = /\/rcvideo\/v1\/bridges\/[^/]+\/meetings(\?.*)?$/;
const POST_VFS_JOIN = /\/rcvideo\/v1\/join\/[^/]+$/;

type HttpEventPair = {
    startEvent: JoinNetworkStartEventTypes;
    endEvent: JoinNetworkEndEventTypes;
};

type ResponseData = {
    ok: boolean;
    status: number;
    rcRequestId?: string;
};

const URL_PATTERN_ENTRIES: [RegExp, HttpEventPair][] = [
    [
        POST_BRIDGES,
        {
            startEvent: JoinPerfEventTypes.POST_BRIDGE_START,
            endEvent: JoinPerfEventTypes.POST_BRIDGE_END,
        },
    ],
    [
        GET_PIN,
        {
            startEvent: JoinPerfEventTypes.GET_BRIDGE_START,
            endEvent: JoinPerfEventTypes.GET_BRIDGE_END,
        },
    ],
    [
        POST_VFS_JOIN,
        {
            startEvent: JoinPerfEventTypes.JOIN_REQ_START,
            endEvent: JoinPerfEventTypes.JOIN_REQ_END,
        },
    ],
    [
        POST_MEETING_INFO,
        {
            startEvent: JoinPerfEventTypes.POST_MEETING_START,
            endEvent: JoinPerfEventTypes.POST_MEETING_END,
        },
    ],
];

export const getEventPairByUrl = (url: string): HttpEventPair | undefined =>
    URL_PATTERN_ENTRIES.find(([pattern]) => pattern.test(url))?.[1];

const getCurrentTime = () => Date.now();

const calcDuration = (end: number, start: number) => {
    if (end === 0 || start === 0) {
        return 0;
    }

    return Math.round(Math.max(0, end - start));
};

const collectHttpMetrics = (entry: PerformanceResourceTiming): HttpRequestMetrics => {
    return {
        dns_duration_ms: calcDuration(entry.domainLookupEnd, entry.domainLookupStart),
        tcp_duration_ms: calcDuration(entry.connectEnd, entry.connectStart),
        tls_duration_ms: calcDuration(entry.secureConnectionStart, entry.connectEnd),
        response_duration_ms: entry.responseStart
            ? calcDuration(entry.responseEnd, entry.responseStart)
            : calcDuration(entry.responseEnd, entry.fetchStart),
        duration_ms: Math.round(entry.duration),
    };
};

type TrackJoinFlowFn = (event: JoinNetworkEventInput) => void;

export type RequestTracker = {
    maybeHandleResponse: (url: string, response: Response) => Promise<void>;
    cleanup: () => void;
    getPendingData: () => Map<string, JoinPendingRequest>;
};

/** @deprecated TODO: move http requests tracking to the librct package - here it breaks the abstraction. */
export const createRequestTracker = (
    trackJoinFlow: TrackJoinFlowFn,
    getIsSpanActive: () => boolean
): RequestTracker => {
    const pendingData: Map<string, JoinPendingRequest> = new Map();
    let performanceObserver: PerformanceObserver | null = null;

    const tryToSendEvent = (url: string) => {
        const data = pendingData.get(url);

        const endEventType = data?.endEventType;
        const canSend = data && data.hasResponseData && data.hasPerformanceData && endEventType;
        if (!canSend) return;

        const metrics: Partial<JoinNetworkReqAttributes> = {
            ...data.metrics,
            rcRequestId: data.rcRequestId,
        };

        trackJoinFlow({
            eventType: endEventType,
            attributes: {
                ...metrics,
            },
            ts: data.eventTime || getCurrentTime(),
        });
        pendingData.delete(url);
    };

    const setupObserver = () => {
        if (performanceObserver) {
            performanceObserver.disconnect();
        }

        performanceObserver = new PerformanceObserver(list => {
            list.getEntries().forEach(entry => {
                const eventTypes = getEventPairByUrl(entry.name);

                if (!eventTypes) return;
                const baseTime = performance.timeOrigin;
                const requestSentAt = Math.round(baseTime + entry.startTime);
                const requestEndedAt = Math.round(baseTime + entry.startTime + entry.duration);

                trackJoinFlow({
                    eventType: eventTypes.startEvent,
                    ts: requestSentAt,
                });

                const httpRequestsEndEventType = eventTypes.endEvent;
                const metrics = collectHttpMetrics(entry as PerformanceResourceTiming);
                const data = pendingData.get(entry.name);

                if (!data) {
                    const newData = {
                        endEventType: httpRequestsEndEventType,
                        hasResponseData: false,
                        hasPerformanceData: true,
                        metrics: metrics,
                        eventTime: requestEndedAt,
                    };
                    pendingData.set(entry.name, newData);
                    return;
                }

                data.metrics = {
                    ...data.metrics,
                    ...metrics,
                };
                data.eventTime = requestEndedAt;
                data.hasPerformanceData = true;

                tryToSendEvent(entry.name);
            });
        });

        performanceObserver.observe({ entryTypes: ['resource'] });
    };

    const cleanup = () => {
        if (performanceObserver) {
            performanceObserver.disconnect();
            performanceObserver = null;
        }
        pendingData.clear();
    };

    const handleResponse = async (
        url: string,
        response: ResponseData,
        eventTypes: HttpEventPair
    ) => {
        const eventType = eventTypes.endEvent;
        const responseCode = response.status;
        const error = !response.ok;
        const rcRequestId = response.rcRequestId ?? '';

        const data: JoinPendingRequest | undefined = pendingData.get(url);
        if (!data) {
            const newData: JoinPendingRequest = {
                endEventType: eventType,
                hasResponseData: true,
                hasPerformanceData: error,
                rcRequestId,
                metrics: {
                    response_code: responseCode,
                    error,
                    ...(error
                        ? {
                              duration_ms: 0,
                              dns_duration_ms: 0,
                              tcp_duration_ms: 0,
                              tls_duration_ms: 0,
                              response_duration_ms: 0,
                          }
                        : null),
                },
            };
            pendingData.set(url, newData);

            return;
        }

        data.rcRequestId = rcRequestId;
        data.hasResponseData = true;
        data.hasPerformanceData = error || data.hasPerformanceData;
        data.metrics = {
            ...data.metrics,
            response_code: responseCode,
            error,
        };

        tryToSendEvent(url);
    };

    /** @deprecated TODO: move http requests tracking to the librct package - here it breaks the abstraction. */
    const maybeHandleResponse = async (url: string, response: ResponseData) => {
        if (!url || !getIsSpanActive()) return;

        try {
            const eventTypes = getEventPairByUrl(url);
            if (!eventTypes) return;

            if (!performanceObserver) {
                setupObserver();
            }

            await handleResponse(url, response, eventTypes);
        } catch (error) {
            logger.debug('Error handling response for performance tracking:', error);
        }
    };

    return {
        maybeHandleResponse,
        cleanup,
        getPendingData: () => pendingData,
    };
};
